import { DocumentServicesObject } from '@wix/document-services-types';
import { PublicInterfaces } from '@wix/editor-platform-interfaces';
import { PlatformContext } from '../../../types/platformApi';
import { getHostEventData } from '../../utils/getHostEventData';
import { COMPANION_APP_DEPENDENCIES_INSTALL_SPEC } from '../appDependenciesInstallData';
import { ensureAppDependenciesInstalled } from './appDependenciesInstall';

const CSM_REFRESH_DEBOUNCE_MS = 300;
const INTENT_INSTALL_RETRY_MS = 300;

/**
 * Reactively installs missing companion-app runtime dependencies.
 *
 * Listens to two DM signals:
 * - `appUpdateCompleted` (custom + regular streams) — targeted reconcile for one app.
 * - the CSM-refresh callback — coarse, debounced full scan (covers the in-editor
 *   override path, which does not emit `appUpdateCompleted`).
 *
 * Both funnel into one idempotent reconcile, serialized through a queue and
 * deduped by a per-app pending set. Construct via the static `init()`, which
 * gates on the experiment, wires the subscriptions, and runs the initial scan.
 */
export class PlatformAppDependenciesService {
  #documentServicesAPI: DocumentServicesObject;
  #context: PlatformContext;
  #pending = new Set<string>();
  #retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  #queue: Promise<unknown> = Promise.resolve();
  #debounceTimer?: ReturnType<typeof setTimeout>;

  static init(
    documentServicesAPI: DocumentServicesObject,
    context: PlatformContext,
  ): PlatformAppDependenciesService | null {
    if (
      !context.experimentsAPI.enabled(COMPANION_APP_DEPENDENCIES_INSTALL_SPEC)
    ) {
      return null;
    }

    const service = new PlatformAppDependenciesService(
      documentServicesAPI,
      context,
    );
    service.#start();
    return service;
  }

  // Private: the only way to obtain an instance is the static `init`, which
  // gates on the experiment and runs `#start()`. This enforces the invariant
  // that a constructed service is always gated and started.
  private constructor(
    documentServicesAPI: DocumentServicesObject,
    context: PlatformContext,
  ) {
    this.#documentServicesAPI = documentServicesAPI;
    this.#context = context;
  }

  #start() {
    try {
      this.#subscribe();
      if (!this.#hasPendingApps()) {
        this.#reconcileAllInstalledApps();
      }
    } catch (error) {
      console.error(
        'Failed to initialize platform app dependencies service:',
        error,
      );
    }
  }

  #subscribe() {
    // appUpdateCompleted arrives on the custom channel (DM notifyAppsOnCustomEvent);
    // subscribe to the regular channel too, mirroring manifest-services, for safety.
    this.#documentServicesAPI.platform.subscribeOnAppEvents(this.#onAppEvent);
    this.#documentServicesAPI.platform.subscribeOnCustomAppEvents(
      this.#onAppEvent,
    );
    this.#documentServicesAPI.platform.registerClientSpecMapRefreshCallback?.(
      this.#onClientSpecMapRefresh,
    );
  }

  #onAppEvent = (event: string) => {
    const hostEvent = getHostEventData<{ appDefinitionId?: string }>(event);

    if (
      hostEvent.eventType === PublicInterfaces.EventType.appUpdateCompleted &&
      hostEvent.appDefinitionId
    ) {
      this.#scheduleReconcile(hostEvent.appDefinitionId);
    }
  };

  #onClientSpecMapRefresh = () => {
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }
    this.#debounceTimer = setTimeout(() => {
      this.#debounceTimer = undefined;
      if (this.#hasPendingApps()) {
        return;
      }
      this.#reconcileAllInstalledApps();
    }, CSM_REFRESH_DEBOUNCE_MS);
  };

  #hasPendingApps = () => {
    return !!this.#documentServicesAPI.tpa.getPendingApps().length;
  };

  #isIntentInstallRunning = () => {
    return this.#context.intentInstallServices?.isIntentInstallRunning();
  };

  #reconcileAllInstalledApps = () => {
    this.#documentServicesAPI.platform
      .getInstalledApps()
      .map((app) => app.appDefinitionId)
      .filter((appDefinitionId): appDefinitionId is string =>
        Boolean(appDefinitionId),
      )
      .forEach((appDefinitionId) => {
        this.#scheduleReconcile(appDefinitionId);
      });
  };

  #scheduleReconcileRetry = (appDefinitionId: string) => {
    if (this.#retryTimers.has(appDefinitionId)) {
      return;
    }

    const timer = setTimeout(() => {
      this.#retryTimers.delete(appDefinitionId);
      this.#scheduleReconcile(appDefinitionId);
    }, INTENT_INSTALL_RETRY_MS);

    this.#retryTimers.set(appDefinitionId, timer);
  };

  // IntentInstallServices is single-active and rejects concurrent
  // Dependency_Service installs (IntentInstallIsAlreadyRunning). Serialize all
  // reconciles through one queue so each install fully completes before the next
  // starts; #pending also dedupes an app that is already queued or installing.
  #scheduleReconcile = (appDefinitionId: string) => {
    if (!appDefinitionId || this.#pending.has(appDefinitionId)) {
      return;
    }

    this.#pending.add(appDefinitionId);
    this.#queue = this.#queue
      .then(() => this.#reconcileApp(appDefinitionId))
      .finally(() => {
        this.#pending.delete(appDefinitionId);
      });
  };

  #reconcileApp = async (appDefinitionId: string) => {
    if (this.#isIntentInstallRunning()) {
      this.#scheduleReconcileRetry(appDefinitionId);
      return;
    }

    try {
      await ensureAppDependenciesInstalled(
        this.#context,
        this.#documentServicesAPI,
        appDefinitionId,
      );
    } catch (error) {
      console.error('Failed to install companion app dependencies:', error);
    }
  };
}
